import { useMemo, useState } from 'react';
import { createFileRoute, Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import {
  ArrowLeft,
  AlertCircle,
  RefreshCw,
  MoreVertical,
  MoveRight,
  FileText,
  BriefcaseBusiness,
  XCircle,
  Ban,
  Phone,
  Mail,
  MessageSquare,
  Timer,
  Zap,
  ShieldCheck,
  Activity,
  Pencil,
  CalendarDays,
  Trophy,
  AlignLeft,
  Network,
  GitMerge,
  ClipboardList,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  formatPriority,
  formatSource,
  formatUserName,
  isWinningOutcome,
  leadPriorityDotClass,
} from '@/lib/leads/leadFormat';
import { LeadValueKeyFact } from '@/components/organisms/leads/LeadValueKeyFact';
import { getLeadNextStepCopy } from '@/components/organisms/leads/support/lead-next-step';
import { ESTIMATE_DETAIL_SEARCH } from '@/components/organisms/contacts/detail/contact-detail-search';
import { formatDistanceToNow } from 'date-fns';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import { StatusBadge } from '@/components/molecules/StatusBadge';
import { Card, CardContent } from '@/components/atoms/card';
import { Skeleton } from '@/components/atoms/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/atoms/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/atoms/tooltip';
import { DetailPageLayout, useWorkspaceHeader } from '@/components/layouts';
import { useContactComms } from '@/lib/contacts/contactComms';
import {
  getContactEmail,
  getContactPhone,
} from '@/components/organisms/contacts/contact-list-utils';
import { useLead } from '@/hooks/leads/useLead';
import { useLeadStages } from '@/hooks/leads/useLeadStages';
import { useConvertLeadToJob } from '@/hooks/leads/useConvertLeadToJob';
import { useConvertToEstimate } from '@/hooks/leads/useConvertToEstimate';
import { useUpdateLeadStage } from '@/hooks/leads/useUpdateLeadStage';
import { useUser } from '@/hooks/settings/useUser';
import { useUsers } from '@/hooks/settings/useUsers';
import { MarkLeadLostModal } from '@/components/organisms/leads/MarkLeadLostModal';
import { DisqualifyLeadModal } from '@/components/organisms/leads/DisqualifyLeadModal';
import { MergeLeadModal } from '@/components/organisms/leads/MergeLeadModal';
import { LeadAssignmentPanel } from '@/components/organisms/leads/AssignmentHistory';
import { LeadContactLink } from '@/components/organisms/leads/LeadContactLink';
import { LeadAssignmentControls } from '@/components/organisms/leads/LeadAssignmentControls';
import { LeadActivityTab } from '@/components/organisms/leads/LeadActivityTab';
import { LeadTasksTab } from '@/components/organisms/leads/LeadTasksTab';
import { LeadFormSubmissionsTab } from '@/components/organisms/leads/LeadFormSubmissionsTab';
import { useLeadFormSubmissions } from '@/hooks/leads/useLeadFormSubmissions';
import { resolveLeadFormTab, type LeadDetailTab } from '@/lib/leads/lead-form-tab';
import type { Lead } from '@/types/lead';

const LEAD_DETAIL_TABS = [
  'overview',
  'activity',
  'tasks',
  'form',
] as const satisfies readonly LeadDetailTab[];

export const Route = createFileRoute('/_dashboard/dashboard/leads/$leadId')({
  validateSearch: (search): { tab: LeadDetailTab } => ({
    tab: LEAD_DETAIL_TABS.includes(search.tab as LeadDetailTab)
      ? (search.tab as LeadDetailTab)
      : 'overview',
  }),
  component: LeadDetailPage,
});

// ─── Helpers ────────────────────────────────────────────────────────────────

const GRADE_STYLES: Record<string, { bg: string; text: string; ring: string }> = {
  A: { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  B: { bg: 'bg-blue-50', text: 'text-blue-700', ring: 'ring-blue-200' },
  C: { bg: 'bg-amber-50', text: 'text-amber-700', ring: 'ring-amber-200' },
  D: { bg: 'bg-orange-50', text: 'text-orange-700', ring: 'ring-orange-200' },
  F: { bg: 'bg-red-50', text: 'text-red-700', ring: 'ring-red-200' },
};

// ─── Status pills ─────────────────────────────────────────────────────────
// A single pill treatment for every status field (Qualification, Urgency, SLA,
// Outcome). Same shape/size — only the semantic tone (color) differs.

type PillTone = 'success' | 'warning' | 'destructive' | 'neutral';

const PILL_TONES: Record<PillTone, string> = {
  success:
    'border-transparent bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400',
  warning:
    'border-transparent bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400',
  destructive: 'border-transparent bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400',
  neutral: 'border-transparent bg-secondary text-secondary-foreground',
};

function StatusPill({
  tone,
  icon: Icon,
  children,
}: {
  tone: PillTone;
  icon?: typeof Timer;
  children: React.ReactNode;
}) {
  return (
    <Badge className={cn('gap-1', PILL_TONES[tone])}>
      {Icon ? <Icon className="size-3" /> : null}
      {children}
    </Badge>
  );
}

function qualificationTone(status: string): PillTone {
  if (status === 'QUALIFIED') return 'success';
  if (status === 'DISQUALIFIED') return 'destructive';
  return 'neutral';
}

function urgencyTone(urgency: string): PillTone {
  if (urgency === 'URGENT' || urgency === 'EMERGENCY') return 'destructive';
  if (urgency === 'HIGH') return 'warning';
  return 'neutral';
}

function formatSla(lead: Lead): { text: string; tone: PillTone; icon: typeof Timer } {
  if (lead.firstResponseAt && lead.timeToFirstResponse != null) {
    const mins = Math.floor(lead.timeToFirstResponse / 60);
    const secs = lead.timeToFirstResponse % 60;
    return { text: `${mins}m ${secs}s`, tone: 'success', icon: ShieldCheck };
  }
  if (lead.slaBreached) return { text: 'Breached', tone: 'destructive', icon: Zap };
  if (lead.isClosed) return { text: 'Closed', tone: 'neutral', icon: Timer };
  return { text: 'Pending', tone: 'warning', icon: Timer };
}

function hasEnrichmentFlags(lead: Lead) {
  const flags = lead.enrichmentFlags;
  return Boolean(
    flags?.isVip || flags?.blockedContact || flags?.serviceCategoryAutoMapped || flags?.multiSource,
  );
}

/**
 * A single enrichment signal as a compact pill. NOT-484: the visible label is
 * concise so the enrichment stat aligns with the sibling stat pills on one line
 * (icon vertically centered) instead of wrapping into a tall, congested block.
 * When the label hides extra detail (membership plan, source list, the
 * "Auto-mapped:" prefix), the full text is exposed via the app Tooltip — which,
 * unlike a native `title`, is keyboard-focusable and announced to screen readers.
 */
function EnrichmentBadge({
  icon: Icon,
  variant,
  className,
  short,
  full,
}: {
  icon: typeof ShieldCheck;
  variant: 'outline' | 'secondary' | 'destructive';
  className?: string;
  short: string;
  full: string;
}) {
  // No hidden detail → plain pill, no tooltip (avoids an empty description).
  const hasHiddenDetail = full !== short;
  const badge = (
    <Badge
      variant={variant}
      className={cn('max-w-full min-w-0 items-center', className)}
      // A Badge renders a <span>, which isn't focusable. When it backs a
      // tooltip, make it focusable so keyboard users can reveal the hidden
      // detail (and screen readers reach the tooltip's aria-describedby).
      tabIndex={hasHiddenDetail ? 0 : undefined}
    >
      <Icon className="mr-1 size-3 shrink-0" />
      <span className="min-w-0 truncate">{short}</span>
    </Badge>
  );
  if (!hasHiddenDetail) return badge;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent>{full}</TooltipContent>
    </Tooltip>
  );
}

function LeadEnrichmentBadges({ lead }: { lead: Lead }) {
  const flags = lead.enrichmentFlags;
  if (!flags) return null;

  const memberShort = flags.isMember ? 'Member' : 'VIP';
  const memberFull = `${memberShort}${
    flags.membershipPlanName ? ` · ${flags.membershipPlanName}` : ''
  }`;
  const categoryShort = flags.serviceCategoryName ?? 'Auto-mapped';
  const categoryFull = flags.serviceCategoryName
    ? `Auto-mapped: ${flags.serviceCategoryName}`
    : 'Auto-mapped category';
  const multiSourceFull = flags.relatedLeadSources?.length
    ? `Multi-source: ${flags.relatedLeadSources.map(formatSource).join(', ')}`
    : 'Multi-source';

  return (
    <div className="flex min-w-0 flex-wrap gap-1.5">
      {flags.isVip ? (
        <EnrichmentBadge
          icon={ShieldCheck}
          variant="outline"
          className="border-emerald-200 bg-emerald-50 text-emerald-700"
          short={memberShort}
          full={memberFull}
        />
      ) : null}
      {flags.blockedContact ? (
        <EnrichmentBadge icon={Ban} variant="destructive" short="Blocked" full="Blocked contact" />
      ) : null}
      {flags.serviceCategoryAutoMapped ? (
        <EnrichmentBadge
          icon={BriefcaseBusiness}
          variant="secondary"
          short={categoryShort}
          full={categoryFull}
        />
      ) : null}
      {flags.multiSource ? (
        <EnrichmentBadge
          icon={Network}
          variant="outline"
          className="border-sky-200 bg-sky-50 text-sky-700"
          short="Multi-source"
          full={multiSourceFull}
        />
      ) : null}
    </div>
  );
}

// ─── Compact detail field ───────────────────────────────────────────────────

function DetailField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0 py-1.5">
      <span className="block text-xs text-muted-foreground">{label}</span>
      <div className="mt-1 min-w-0 text-sm">{children}</div>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

// Injects the entity header ("Lead LD-…") into the global workspace header bar,
// mirroring EstimateContextHeaderSlot.
function LeadContextHeaderSlot({ lead }: { lead: Lead }) {
  const contextHeader = useMemo(() => {
    const subtitle = [lead.stage.name, formatSource(lead.source)].filter(Boolean).join(' · ');
    return (
      <div className="flex min-w-0 flex-col">
        <h2 className="truncate text-base font-semibold">Lead {lead.leadNumber}</h2>
        <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
      </div>
    );
  }, [lead.leadNumber, lead.stage.name, lead.source]);
  useWorkspaceHeader({ contextHeader }, [contextHeader]);
  return null;
}

interface PipelineStage {
  id: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
  isWon: boolean;
  isLost: boolean;
}

// Configurable lead-stage pipeline (TenantLeadStage), mirroring the Estimate
// status pipeline. Highlights the current stage; earlier stages read as done.
function LeadStagePipeline({
  stages,
  currentId,
  disabled = false,
  onSelectStage,
}: {
  stages: PipelineStage[];
  currentId: string;
  disabled?: boolean;
  onSelectStage: (stageId: string) => void;
}) {
  const ordered = [...stages]
    .filter((s) => s.isActive || s.id === currentId)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  if (ordered.length === 0) return null;
  const currentIndex = ordered.findIndex((s) => s.id === currentId);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {ordered.map((stage, index) => {
        const isCurrent = stage.id === currentId;
        const done = currentIndex >= 0 && index <= currentIndex;
        const isTerminal = stage.isWon || stage.isLost;
        const canSelect = !disabled && !isCurrent && !isTerminal;
        return (
          <button
            key={stage.id}
            type="button"
            disabled={!canSelect}
            onClick={() => {
              if (canSelect) onSelectStage(stage.id);
            }}
            title={isTerminal ? 'Use the lead detail actions to close a lead.' : undefined}
            className={cn(
              'rounded-md px-2 py-1 text-xs font-medium transition-colors',
              isCurrent
                ? 'bg-primary text-primary-foreground'
                : done
                  ? 'bg-muted text-foreground'
                  : 'bg-muted/40 text-muted-foreground',
              canSelect && 'cursor-pointer hover:bg-muted hover:text-foreground',
              !canSelect && !isCurrent && 'cursor-not-allowed opacity-70',
            )}
          >
            {stage.name}
          </button>
        );
      })}
    </div>
  );
}

/** Semibold value tint for the SLA key-fact, keyed by its status tone. Keeps
 *  the tile one clean line (matching its siblings) while the color still
 *  carries the breached / pending / met signal. */
const SLA_TONE_TEXT: Record<PillTone, string> = {
  success: 'text-emerald-600',
  warning: 'text-amber-600',
  destructive: 'text-red-600',
  neutral: 'text-foreground',
};

function KeyFact({
  label,
  value,
  withBorder,
  valueClassName,
}: {
  label: string;
  value: string;
  withBorder?: boolean;
  valueClassName?: string;
}) {
  return (
    <div className={cn('min-w-0', withBorder && '@[44rem]:border-l @[44rem]:pl-5')}>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className={cn('truncate text-base font-semibold', valueClassName)} title={value}>
        {value}
      </p>
    </div>
  );
}

function LeadDetailPage() {
  const { leadId } = Route.useParams();
  const { tab } = Route.useSearch();
  const router = useRouter();
  const companyTimeZone = useCompanyTimezone();
  const { location } = useRouterState();
  const { callNumber, messageContact } = useContactComms();
  const leadQuery = useLead(leadId);
  const stagesQuery = useLeadStages();
  const convertToJob = useConvertLeadToJob();
  const convertToEstimate = useConvertToEstimate();
  const updateStage = useUpdateLeadStage();
  const usersQuery = useUsers();
  // Resolve the assignee by id (single fetch, pagination-proof) so an owner
  // beyond the first roster page still shows their name. The roster above is
  // retained for the assignment-history timeline (which needs many names).
  const assigneeQuery = useUser(leadQuery.data?.assignedTo ?? '');
  // Any lead can carry intake submissions: one from someone with an open
  // lead is appended to it whatever the lead's source.
  const formSubmissionsQuery = useLeadFormSubmissions(leadId);

  const [markLostOpen, setMarkLostOpen] = useState(false);
  const [disqualifyOpen, setDisqualifyOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);

  const lead = leadQuery.data;
  const isExactRoute = location.pathname === `/dashboard/leads/${leadId}`;

  function handleConvertToEstimate() {
    convertToEstimate.mutate(leadId, {
      onSuccess: (data) => {
        void router.navigate({
          to: '/dashboard/estimates/$id',
          params: { id: data.estimateId },
          search: ESTIMATE_DETAIL_SEARCH,
        });
      },
    });
  }

  function handleConvertToJob() {
    convertToJob.mutate(leadId, {
      onSuccess: (data) => {
        void router.navigate({
          to: '/dashboard/jobs/$id/edit',
          params: { id: data.jobId },
        });
      },
    });
  }

  if (!isExactRoute) {
    return <Outlet />;
  }

  // Loading
  if (leadQuery.isLoading) {
    return (
      <div className="@container p-6 space-y-4">
        <Skeleton className="h-10 w-64" />
        {/* Mirror the live key-facts grid (2-up, 4-up at @44rem) so the layout
            doesn't reflow when the lead resolves. */}
        <div className="grid grid-cols-2 gap-4 @[44rem]:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-14" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  // Error
  if (leadQuery.isError || !lead) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <div>
          <p className="text-lg font-semibold">Lead not found</p>
          <p className="text-sm text-muted-foreground">
            This lead doesn't exist or has been deleted.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => void leadQuery.refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" /> Retry
          </Button>
          <Button onClick={() => void router.navigate({ to: '/dashboard/leads' })}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Leads
          </Button>
        </div>
      </div>
    );
  }

  // Derived
  const contactName = lead.contact
    ? (lead.contact.displayName ??
      `${lead.contact.firstName}${lead.contact.lastName ? ' ' + lead.contact.lastName : ''}`)
    : (lead.title ?? 'Untitled Lead');
  // Resolve through the canonical contact points (primaryEmail/primaryPhone,
  // then the emails/phoneNumbers arrays) — a synced contact often has the
  // legacy scalars empty, which otherwise hides the Call/Email quick-actions.
  const contactEmail = lead.contact ? getContactEmail(lead.contact) : '';
  const contactPhone = lead.contact ? getContactPhone(lead.contact) : '';
  const sla = formatSla(lead);
  const stages = stagesQuery.data ?? [];
  const users = usersQuery.data?.data ?? [];
  const assignedUser = assigneeQuery.data ?? users.find((user) => user.id === lead.assignedTo);
  // names, not IDs — prefer the roster user, then the backend-resolved name
  // (NOT-486: owners who have left the company are absent from the roster but
  // still resolve server-side), then 'Unassigned' — never the raw UUID.
  const assignedLabel = lead.assignedTo
    ? formatUserName(assignedUser, lead.assignedToName ?? 'Unassigned')
    : 'Unassigned';
  const gradeStyle = lead.leadGrade ? GRADE_STYLES[lead.leadGrade] : null;
  const { activeTab, showFormTab } = resolveLeadFormTab({
    requestedTab: tab,
    formSubmissionCount: formSubmissionsQuery.data?.length ?? 0,
    isError: formSubmissionsQuery.isError,
  });
  // NOT-445: a "self-booked" lead (booked at creation) was never worked, so its
  // priority grade / freshness are noise — show a self-booked marker instead.
  const isSelfBooked = lead.autoCreatedReason === 'DIRECT_JOB';
  const sourceLabel = formatSource(lead.source);
  // NOT-483: the customer name is the primary heading; the lead number is
  // demoted into the meta line alongside priority (source lives in key facts).

  return (
    <TooltipProvider delayDuration={200}>
      <DetailPageLayout key={leadId}>
        <LeadContextHeaderSlot lead={lead} />

        {/* Detail header card — title + stage + meta + actions, then key facts */}
        <section className="@container rounded-xl border bg-card p-5 shadow-sm">
          {/* Title and the (now two compact) action controls share one row so
              they read as a single aligned header. Only a true phone-width
              container stacks them, so the meta line ("Lead … · Medium
              Priority") is never squeezed into an awkward wrap. */}
          <div className="flex flex-col gap-3 @[32rem]:flex-row @[32rem]:items-start @[32rem]:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <h2 className="truncate text-xl font-semibold">{contactName}</h2>
                <StatusBadge
                  status={lead.stage.name}
                  label={lead.stage.name}
                  color={lead.stage.color}
                  className="rounded-md"
                />
              </div>
              <p className="mt-1 flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
                <span>Lead {lead.leadNumber}</span>
                <span aria-hidden className="text-muted-foreground/50">
                  ·
                </span>
                <span
                  aria-hidden
                  className={cn('size-2 rounded-full', leadPriorityDotClass(lead.priority))}
                />
                <span className="capitalize">{formatPriority(lead.priority)} priority</span>
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
              {!lead.isClosed ? (
                <LeadAssignmentControls
                  leadId={lead.id}
                  assignedTo={lead.assignedTo}
                  assignedToLabel={
                    assignedUser ? formatUserName(assignedUser) : (lead.assignedToName ?? undefined)
                  }
                />
              ) : null}
              {lead.convertedJobId || !lead.isClosed ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon-sm">
                      <MoreVertical size={15} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {!lead.isClosed ? (
                      <DropdownMenuItem
                        onClick={() =>
                          void router.navigate({
                            to: '/dashboard/leads/$leadId/edit',
                            params: { leadId },
                            search: { tab: 'overview' },
                          })
                        }
                      >
                        <Pencil size={14} className="mr-2" /> Edit
                      </DropdownMenuItem>
                    ) : null}
                    {lead.convertedJobId ? (
                      <DropdownMenuItem
                        onClick={() =>
                          void router.navigate({
                            to: '/dashboard/jobs/$id',
                            params: { id: lead.convertedJobId! },
                          })
                        }
                      >
                        <BriefcaseBusiness size={14} className="mr-2" /> View job
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        onClick={handleConvertToJob}
                        disabled={convertToJob.isPending}
                      >
                        <BriefcaseBusiness size={14} className="mr-2" /> Convert to job
                      </DropdownMenuItem>
                    )}
                    {!lead.isClosed ? (
                      <DropdownMenuItem onClick={() => setMergeOpen(true)}>
                        <GitMerge size={14} className="mr-2" /> Merge duplicate
                      </DropdownMenuItem>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
            </div>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-4 border-t pt-4 @[44rem]:grid-cols-4">
            <div className="min-w-0">
              <p className="text-sm text-muted-foreground">Contact</p>
              {lead.contact ? (
                <LeadContactLink
                  name={contactName}
                  company={lead.contact.companyName}
                  email={contactEmail}
                  phone={contactPhone}
                  onView={() =>
                    void router.navigate({
                      to: '/dashboard/contacts/$contactId',
                      params: { contactId: lead.contact!.id },
                    })
                  }
                />
              ) : (
                <p className="truncate text-base font-semibold" title={contactName}>
                  {contactName}
                </p>
              )}
            </div>
            <LeadValueKeyFact lead={lead} withBorder />
            <KeyFact label="Source" value={sourceLabel} withBorder />
            {/* SLA is the most time-critical lead metric, so it earns the top
                glance strip; the current owner has its own dedicated panel
                below, which made this tile a redundant repeat. */}
            <KeyFact
              label="SLA"
              value={sla.text}
              valueClassName={SLA_TONE_TEXT[sla.tone]}
              withBorder
            />
          </div>
          {/* Contact quick-actions, kept in the header so reaching the customer
              (call / text / email) never requires scrolling to the contact card. */}
          {lead.contact && (contactPhone || contactEmail) ? (
            <div className="mt-4 flex flex-wrap gap-2 border-t pt-4">
              {contactPhone ? (
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => callNumber(contactPhone)}
                >
                  <Phone className="size-4" /> Call
                </Button>
              ) : null}
              {/* Text is an SMS action, so it needs a phone (matches the table
                  row's `enabled={Boolean(phone)}`) — an email-only contact must
                  not get a dead SMS button. */}
              {contactPhone ? (
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => void messageContact(lead.contact!.id)}
                >
                  <MessageSquare className="size-4" /> Text
                </Button>
              ) : null}
              {contactEmail ? (
                <Button variant="outline" size="sm" asChild>
                  <a href={`mailto:${contactEmail}`}>
                    <Mail className="size-4" /> Email
                  </a>
                </Button>
              ) : null}
            </div>
          ) : null}
        </section>

        {/* Next step + stage pipeline */}
        <section className="space-y-3 rounded-xl border bg-muted/45 p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
                <MoveRight size={12} />
                Next step
              </p>
              <p className="mt-1 text-[15px] font-medium text-foreground">
                {getLeadNextStepCopy(lead, companyTimeZone)}
              </p>
            </div>
            {lead.convertedEstimateId ? (
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() =>
                  void router.navigate({
                    to: '/dashboard/estimates/$id',
                    params: { id: lead.convertedEstimateId! },
                    search: ESTIMATE_DETAIL_SEARCH,
                  })
                }
              >
                <FileText className="size-4" />
                View estimate
              </Button>
            ) : !lead.isClosed ? (
              <Button
                size="sm"
                className="shrink-0"
                disabled={convertToEstimate.isPending}
                onClick={handleConvertToEstimate}
              >
                <FileText className="size-4" />
                Convert to estimate
              </Button>
            ) : null}
          </div>
          <LeadStagePipeline
            stages={stages}
            currentId={lead.stage.id}
            disabled={updateStage.isPending || lead.isClosed}
            onSelectStage={(stageId) => updateStage.mutate({ id: lead.id, stageId })}
          />
          {!lead.isClosed ? (
            <div className="flex flex-wrap items-center gap-2 border-t pt-3 text-xs text-muted-foreground">
              <span>Other actions:</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setDisqualifyOpen(true)}
              >
                <Ban size={13} /> Disqualify
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-red-600 hover:text-red-600"
                onClick={() => setMarkLostOpen(true)}
              >
                <XCircle size={13} /> Mark lost
              </Button>
            </div>
          ) : null}
        </section>

        {/* Assignment history timeline — the current owner floats to the top,
            marked CURRENT. The active assignee already appears in the header
            key-facts, so a separate summary row here would just repeat it (and
            its avatar indent misaligned the name against the timeline below). */}
        <section className="rounded-xl border bg-card p-5 shadow-sm">
          <LeadAssignmentPanel
            assignedTo={lead.assignedTo}
            assignedLabel={assignedLabel}
            assignedBy={lead.assignedBy}
            history={lead.assignmentHistory}
            users={users}
          />
        </section>

        <Tabs
          value={activeTab}
          onValueChange={(nextTab) =>
            void router.navigate({
              search: (prev) => ({ ...prev, tab: nextTab as LeadDetailTab }) as never,
              replace: true,
            })
          }
        >
          <TabsList variant="line" className="w-full justify-start bg-background">
            <TabsTrigger value="overview">
              <FileText className="size-3.5" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="activity">
              <Activity className="size-3.5" />
              Activity
            </TabsTrigger>
            <TabsTrigger value="tasks">
              <CalendarDays className="size-3.5" />
              Tasks
            </TabsTrigger>
            {showFormTab ? (
              <TabsTrigger value="form">
                <ClipboardList className="size-3.5" />
                Form
              </TabsTrigger>
            ) : null}
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            <Card>
              <CardContent className="@container space-y-5 p-4 sm:p-5">
                {/* Uniform attribute grid — container-query columns so the
                    drawer/split widths (not the viewport) drive the layout, and
                    items-start so a taller Enrichment cell doesn't stretch its row. */}
                <div className="grid grid-cols-2 items-start gap-x-6 gap-y-4 @[34rem]:grid-cols-3 @[52rem]:grid-cols-4">
                  <DetailField label="Qualification">
                    <StatusPill tone={qualificationTone(lead.qualificationStatus)}>
                      {formatSource(lead.qualificationStatus)}
                    </StatusPill>
                  </DetailField>

                  {hasEnrichmentFlags(lead) ? (
                    <DetailField label="Enrichment">
                      <LeadEnrichmentBadges lead={lead} />
                    </DetailField>
                  ) : null}

                  {isSelfBooked ? (
                    <DetailField label="Priority score">
                      <StatusPill tone="success">Self-booked — no action</StatusPill>
                    </DetailField>
                  ) : (
                    lead.leadGrade && (
                      <DetailField label="Priority score">
                        <div className="flex items-center gap-2">
                          <span
                            className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ring-1 ${
                              gradeStyle
                                ? `${gradeStyle.bg} ${gradeStyle.text} ${gradeStyle.ring}`
                                : 'bg-muted text-muted-foreground ring-border'
                            }`}
                          >
                            {lead.leadGrade}
                          </span>
                          <span className="text-xs text-muted-foreground">{lead.leadScore}</span>
                          {lead.freshness ? (
                            <Badge variant="secondary" className="capitalize">
                              {lead.freshness.toLowerCase()}
                            </Badge>
                          ) : null}
                        </div>
                      </DetailField>
                    )
                  )}

                  <DetailField label="Urgency">
                    <StatusPill tone={urgencyTone(lead.urgency)}>
                      {formatSource(lead.urgency)}
                    </StatusPill>
                  </DetailField>

                  <DetailField label="SLA">
                    <StatusPill tone={sla.tone} icon={sla.icon}>
                      {sla.text}
                    </StatusPill>
                  </DetailField>

                  <DetailField label="Created">
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(lead.createdAt), { addSuffix: true })}
                    </span>
                  </DetailField>

                  <DetailField label="Follow-up">
                    {lead.followUpDate ? (
                      <span className="text-sm">
                        {formatDateTimeInTimeZone(lead.followUpDate, companyTimeZone, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">Not scheduled</span>
                    )}
                  </DetailField>

                  {lead.lastActivityAt && (
                    <DetailField label="Last activity">
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(lead.lastActivityAt), {
                          addSuffix: true,
                        })}
                      </span>
                    </DetailField>
                  )}

                  {lead.isClosed && lead.outcome && (
                    <DetailField label="Outcome">
                      <StatusPill
                        tone={isWinningOutcome(lead.outcome) ? 'success' : 'destructive'}
                        icon={isWinningOutcome(lead.outcome) ? Trophy : undefined}
                      >
                        {formatSource(lead.outcome)}
                      </StatusPill>
                    </DetailField>
                  )}
                </div>

                {/* Request (left) + Contact card (right) */}
                <div className="border-t pt-5">
                  <div className="space-y-4">
                    <div>
                      <p className="mb-1 text-xs text-muted-foreground">Service request</p>
                      {lead.serviceRequested ? (
                        <p className="whitespace-pre-wrap break-words text-sm">
                          {lead.serviceRequested}
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground">Not specified</p>
                      )}
                    </div>

                    {/* NOT-478: lead service address */}
                    <div>
                      <p className="mb-1 text-xs text-muted-foreground">Service address</p>
                      {lead.serviceAddress ? (
                        <p className="break-words text-sm">
                          {[
                            lead.serviceAddress.street,
                            lead.serviceAddress.street2,
                            [
                              lead.serviceAddress.city,
                              lead.serviceAddress.state,
                              lead.serviceAddress.zip,
                            ]
                              .filter(Boolean)
                              .join(' '),
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground">Not specified</p>
                      )}
                    </div>

                    <div>
                      <p className="mb-1.5 text-xs text-muted-foreground">Description</p>
                      {lead.description ? (
                        <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">
                          {lead.description}
                        </p>
                      ) : (
                        <div className="flex items-center gap-2 rounded-md border border-dashed bg-muted/30 px-3 py-4 text-sm text-muted-foreground">
                          <AlignLeft className="size-4 shrink-0" />
                          No description added yet.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="activity" className="mt-4">
            <LeadActivityTab leadId={leadId} readOnly />
          </TabsContent>

          <TabsContent value="tasks" className="mt-4">
            <LeadTasksTab leadId={leadId} contactId={lead.contactId} readOnly />
          </TabsContent>

          {showFormTab ? (
            <TabsContent value="form" className="mt-4">
              <LeadFormSubmissionsTab leadId={leadId} />
            </TabsContent>
          ) : null}
        </Tabs>

        <MarkLeadLostModal
          open={markLostOpen}
          onOpenChange={setMarkLostOpen}
          leadId={leadId}
          onSuccess={() => void leadQuery.refetch()}
        />
        <DisqualifyLeadModal
          open={disqualifyOpen}
          onOpenChange={setDisqualifyOpen}
          leadId={leadId}
          onSuccess={() => void leadQuery.refetch()}
        />
        <MergeLeadModal
          open={mergeOpen}
          onOpenChange={setMergeOpen}
          survivor={lead}
          onSuccess={() => void leadQuery.refetch()}
        />
      </DetailPageLayout>
    </TooltipProvider>
  );
}
